/**
 * job_select_controller.js — colour-coded, status-bearing job picker (Choices.js).
 *
 * Like the generic `select` controller it wraps a native <select> with Choices and
 * inherits VendorWidgetController's lifecycle (bind to the CONTAINER, let Choices
 * reparent the child <select> — no reconnect-freeze, Turbo-cache teardown for free).
 *
 * What it adds: Choices ignores per-option styling when it reads a plain <select>
 * (it takes only value + text). So we feed Choices a `choices` ARRAY built from each
 * <option>'s data-* attributes, giving every option:
 *   - labelClass        -> a status colour (swms-job-opt--<status>)
 *   - labelDescription  -> a muted sub-line (status, suburb, description)
 *
 *   <div data-controller="job-select"
 *        data-placeholder="No linked job"
 *        data-search-placeholder="Search jobs..."
 *        data-title-input="swmsTitle">          <!-- optional: id of a title field to auto-fill -->
 *     <select name="job_id">
 *       <option value="">No linked job</option>
 *       <option value="42" data-status-color="primary" data-title="Tree removal, Hornsby"
 *               data-desc="Ready · Hornsby · Remove gum trees">#42 Smith Family</option>
 *     </select>
 *   </div>
 *
 * Title auto-fill: when data-title-input names a field, selecting a job copies the
 * chosen option's data-title into it — but only while the field is empty or still
 * holds our last suggestion, so a title the user typed is never clobbered.
 */
import VendorWidgetController from "./_vendor_widget.js";

export default class extends VendorWidgetController {
        retryCount() { return 5; }

        vendorReady() { return typeof Choices !== 'undefined'; }

        widgetElement() { return this.element.querySelector('select'); }

        buildWidget(select) {
            // Capture the server-rendered options BEFORE Choices rebuilds them:
            //   - `choices`: the rich array (Choices ignores config.choices for a
            //     <select>, so we apply it via setChoices() after init).
            //   - `titleByValue`: value -> suggested title (Choices' rebuilt <option>
            //     elements drop our data-title, so autofill reads from this map).
            var choices = [];
            this._titleByValue = {};
            var titleByValue = this._titleByValue;
            Array.prototype.forEach.call(select.options, function (opt) {
                // status_display_color is a Bootstrap token (primary/success/...),
                // the same one the app's status badges use -> match via text-<token>.
                var color = opt.dataset.statusColor || '';
                choices.push({
                    value: opt.value,
                    label: (opt.textContent || '').trim(),
                    selected: opt.selected,
                    disabled: opt.disabled,
                    // Empty-value row is the "clear" placeholder; real jobs carry colour.
                    placeholder: opt.value === '',
                    labelClass: color ? ('swms-job-opt text-' + color) : '',
                    labelDescription: opt.dataset.desc || '',
                });
                titleByValue[opt.value] = opt.dataset.title || '';
            });

            var ds = this.element.dataset;
            var instance = new Choices(select, {
                searchEnabled: true,
                shouldSort: false,
                itemSelectText: '',
                allowHTML: false,
                placeholderValue: ds.placeholder || 'No linked job',
                searchPlaceholderValue: ds.searchPlaceholder || 'Search jobs...',
            });
            // Replace the plain DOM-derived choices with the rich array so the
            // status colour and sub-line actually render.
            instance.setChoices(choices, 'value', 'label', true);
            select.choicesInstance = instance;

            // Optional: auto-fill a title field from the selected job.
            this._titleInput = ds.titleInput ? document.getElementById(ds.titleInput) : null;
            if (this._titleInput) {
                this._lastSuggestion = '';
                this._onChange = this._fillTitle.bind(this, select);
                select.addEventListener('change', this._onChange);
                // A job already chosen on load (a link from the job page, or a
                // re-render) suggests its title the same way a pick does.
                if (select.value) this._fillTitle(select);
            }
            return instance;
        }

        _fillTitle(select) {
            var suggestion = this._titleByValue[select.value] || '';
            var current = this._titleInput.value.trim();
            // Only overwrite an empty field or one still holding our last suggestion,
            // so a title the user typed themselves is preserved.
            if (current === '' || current === this._lastSuggestion) {
                this._titleInput.value = suggestion;
                this._lastSuggestion = suggestion;
            }
        }

        destroyWidget(instance, select) {
            if (select && this._onChange) {
                select.removeEventListener('change', this._onChange);
                this._onChange = null;
            }
            if (instance && typeof instance.destroy === 'function') {
                try { instance.destroy(); } catch (_e) {}
            }
            if (select) select.choicesInstance = null;
        }
}
